/**
 * ImageGallery Block — Responsive grid of images with source attribution
 *
 * Features:
 *   - Responsive grid: 2 cols mobile, 3-4 on desktop (configurable)
 *   - Source domain badge on each image
 *   - Lazy loading
 *   - Lightbox overlay on click (no external deps)
 */
import React, { useState, useCallback, useEffect } from 'react';
import type { ImageGalleryData } from '../types';

// =============================================================================
// LIGHTBOX
// =============================================================================

interface LightboxProps {
  image: ImageGalleryData['images'][number];
  onClose: () => void;
}

const Lightbox: React.FC<LightboxProps> = ({ image, onClose }) => {
  // Close on Escape key
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={onClose}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-black/60 border border-cyan-400/30 flex items-center justify-center text-cyan-400 hover:bg-cyan-400/20 transition-colors"
        aria-label="Close"
      >
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>

      {/* Image */}
      <div
        className="relative max-w-[90vw] max-h-[85vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={image.url}
          alt={image.alt || 'Image'}
          className="max-w-full max-h-[80vh] object-contain rounded-xl shadow-[0_0_40px_rgba(34,211,238,0.2)]"
        />

        {/* Source attribution */}
        {(image.source || image.sourceDomain) && (
          <div className="mt-3 flex items-center gap-2 text-xs text-cyan-400/60">
            {image.sourceDomain && (
              <img
                src={`https://www.google.com/s2/favicons?domain=${image.sourceDomain}&sz=16`}
                alt=""
                className="w-4 h-4 rounded-sm"
              />
            )}
            <span>{image.source || image.sourceDomain}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// =============================================================================
// COMPONENT
// =============================================================================

interface ImageGalleryProps {
  data: ImageGalleryData;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({ data }) => {
  const { images, columns } = data;
  const [lightboxIdx, setLightboxIdx] = useState<number | null>(null);

  const openLightbox = useCallback((idx: number) => setLightboxIdx(idx), []);
  const closeLightbox = useCallback(() => setLightboxIdx(null), []);

  if (!images || images.length === 0) return null;

  // Grid columns: configurable, defaults to responsive 2→3→4
  const colClass = columns
    ? columns === 2
      ? 'grid-cols-2'
      : columns === 3
        ? 'grid-cols-2 md:grid-cols-3'
        : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4'
    : 'grid-cols-2 md:grid-cols-3 lg:grid-cols-4';

  return (
    <>
      <div className={`grid ${colClass} gap-3`}>
        {images.map((img, idx) => (
          <button
            key={idx}
            onClick={() => openLightbox(idx)}
            className="group relative overflow-hidden rounded-xl border border-cyan-400/15 hover:border-cyan-400/40 bg-black/30 transition-all duration-300 hover:shadow-[0_0_20px_rgba(34,211,238,0.15)] focus:outline-none focus:ring-2 focus:ring-cyan-400/50 aspect-square"
          >
            {/* Image */}
            <img
              src={img.url}
              alt={img.alt || 'Gallery image'}
              loading="lazy"
              className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105 opacity-85 group-hover:opacity-100"
              onError={(e) => {
                (e.target as HTMLImageElement).style.display = 'none';
              }}
            />

            {/* Hover overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

            {/* Source domain badge (bottom-left) */}
            {img.sourceDomain && (
              <span className="absolute bottom-2 left-2 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/70 backdrop-blur-sm border border-cyan-400/20 text-[10px] text-cyan-400/70 font-medium">
                <img
                  src={`https://www.google.com/s2/favicons?domain=${img.sourceDomain}&sz=16`}
                  alt=""
                  className="w-3 h-3 rounded-sm"
                />
                {img.sourceDomain}
              </span>
            )}

            {/* Zoom icon (top-right on hover) */}
            <span className="absolute top-2 right-2 w-7 h-7 rounded-full bg-black/60 backdrop-blur-sm border border-cyan-400/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
              <svg className="w-3.5 h-3.5 text-cyan-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
                <line x1="11" y1="8" x2="11" y2="14" />
                <line x1="8" y1="11" x2="14" y2="11" />
              </svg>
            </span>
          </button>
        ))}
      </div>

      {/* Lightbox */}
      {lightboxIdx !== null && images[lightboxIdx] && (
        <Lightbox image={images[lightboxIdx]} onClose={closeLightbox} />
      )}
    </>
  );
};

export default ImageGallery;
